import { useEffect, useId, useRef, useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useDialog } from './useDialog';

interface Props {
    title: string;
    message: string;
    confirmLabel?: string;
    cancelLabel?: string;
    variant?: 'danger' | 'warning';
    /** May return a promise; while it runs the dialog cannot be cancelled (see below). */
    onConfirm: () => void | Promise<unknown>;
    onCancel: () => void;
}

/**
 * A yes/no question before a write that is hard to take back.
 *
 * Since the design audit of 2026-10-07 (S1-03) it is a real dialog: role, `aria-modal`, label,
 * focus inside, Esc = cancel, focus back to the opener. The panel takes the first focus, not the
 * confirm button — Enter must not confirm by accident.
 *
 * **While a confirm is running, nothing cancels.** Several pages keep this dialog open while
 * their delete is awaited. With Esc and the backdrop now cancelling, closing it mid-request would
 * read as "cancelled" while the delete still went through. So when `onConfirm` returns a promise
 * the buttons disable, Esc and the backdrop do nothing, and the dialog waits for it to settle.
 */
export function ConfirmModal({
    title,
    message,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    variant = 'danger',
    onConfirm,
    onCancel,
}: Props) {
    const panel = useRef<HTMLDivElement>(null);
    const titleId = useId();
    const messageId = useId();
    const [pending, setPending] = useState(false);
    const mounted = useRef(true);
    useEffect(() => () => { mounted.current = false; }, []);

    const cancel = () => { if (!pending) onCancel(); };
    useDialog(panel, cancel, { closeOnEscape: !pending });

    const confirm = () => {
        if (pending) return;
        const result = onConfirm();
        if (result && typeof (result as Promise<unknown>).then === 'function') {
            setPending(true);
            (result as Promise<unknown>).finally(() => { if (mounted.current) setPending(false); });
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="presentation" onClick={cancel}>
            <div
                ref={panel}
                role="alertdialog"
                aria-modal="true"
                aria-labelledby={titleId}
                aria-describedby={messageId}
                aria-busy={pending}
                tabIndex={-1}
                onClick={e => e.stopPropagation()}
                className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
            >
                <div className="flex items-start gap-3">
                    <div className={`rounded-full p-2 ${variant === 'danger' ? 'bg-red-100' : 'bg-yellow-100'}`}>
                        <AlertTriangle aria-hidden="true" className={`h-5 w-5 ${variant === 'danger' ? 'text-red-600' : 'text-yellow-600'}`} />
                    </div>
                    <div className="flex-1">
                        <h3 id={titleId} className="text-lg font-semibold text-gray-900">{title}</h3>
                        <p id={messageId} className="mt-1 text-sm text-gray-600">{message}</p>
                    </div>
                </div>
                <div className="mt-5 flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={cancel}
                        disabled={pending}
                        className="h-8 rounded-md border px-3 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                        {cancelLabel}
                    </button>
                    <button
                        type="button"
                        onClick={confirm}
                        disabled={pending}
                        className={`inline-flex h-8 items-center gap-1.5 rounded-md px-3 text-sm font-medium text-white disabled:opacity-70 ${
                            variant === 'danger'
                                ? 'bg-red-600 hover:bg-red-700'
                                : 'bg-yellow-600 hover:bg-yellow-700'
                        }`}
                    >
                        {pending && <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />}
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}
